import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../lib/api';
import useRealtimeEvents from '../hooks/useRealtimeEvents';
import {
  CLIENT_SKILL_LEVELS,
  PLAYERS_PACKAGE_INCLUSIONS,
  SKILL_FILTER_KIDS,
  SKILL_LEVELS,
  TOURNAMENT_SKILL_LEVELS,
  allTournamentCategories,
  parseExtraCategories,
  setRuntimeExtraCategories,
} from '../constants/tournaments';

const TournamentCatalogContext = createContext(null);

const fallbackCatalog = () => ({
  extraSkillLevels: [],
  extraCategories: [],
  packageInclusions: [...PLAYERS_PACKAGE_INCLUSIONS],
  tournamentSkillLevels: [...TOURNAMENT_SKILL_LEVELS],
  clientSkillLevels: [...CLIENT_SKILL_LEVELS],
  skillLevels: [...SKILL_LEVELS],
  tournamentCategories: allTournamentCategories([]),
});

const parseCatalog = (data) => {
  const extra = Array.isArray(data?.extra_skill_levels) ? data.extra_skill_levels : [];
  const extraSkillLevels = extra.map((item) => (
    typeof item === 'string'
      ? { name: item, hint: '' }
      : { name: String(item?.name || '').trim(), hint: String(item?.hint || '').trim() }
  )).filter((item) => item.name);
  const packageInclusions = Array.isArray(data?.package_inclusions) && data.package_inclusions.length
    ? data.package_inclusions.map((x) => String(x || '').trim()).filter(Boolean)
    : [...PLAYERS_PACKAGE_INCLUSIONS];
  const tournamentSkillLevels = Array.isArray(data?.tournament_skill_levels) && data.tournament_skill_levels.length
    ? data.tournament_skill_levels.map((x) => String(x || '').trim()).filter(Boolean)
    : [...TOURNAMENT_SKILL_LEVELS];
  const clientSkillLevels = Array.isArray(data?.client_skill_levels) && data.client_skill_levels.length
    ? data.client_skill_levels.map((x) => String(x || '').trim()).filter(Boolean)
    : [...CLIENT_SKILL_LEVELS];
  const skillLevels = Array.isArray(data?.skill_levels) && data.skill_levels.length
    ? data.skill_levels.map((x) => String(x || '').trim()).filter(Boolean)
    : [...SKILL_LEVELS];
  const extraCategories = parseExtraCategories(data?.extra_categories);
  setRuntimeExtraCategories(extraCategories);
  return {
    extraSkillLevels,
    extraCategories,
    packageInclusions,
    tournamentSkillLevels,
    clientSkillLevels,
    skillLevels,
    tournamentCategories: allTournamentCategories(extraCategories),
  };
};

export const TournamentCatalogProvider = ({ children }) => {
  const [catalog, setCatalog] = useState(fallbackCatalog);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/tournaments/meta`);
      if (!res.ok) throw new Error('meta');
      const data = await res.json();
      setCatalog(parseCatalog(data));
    } catch {
      setCatalog(fallbackCatalog());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useRealtimeEvents(
    ['tournament_catalog_updated'],
    (data) => {
      if (data?.catalog) {
        setCatalog(parseCatalog(data.catalog));
        setLoading(false);
        return;
      }
      refresh();
    },
  );

  const skillFilterPills = useMemo(
    () => [...catalog.skillLevels, SKILL_FILTER_KIDS],
    [catalog.skillLevels],
  );

  const value = useMemo(() => ({
    ...catalog,
    skillFilterPills,
    loading,
    refresh,
  }), [catalog, skillFilterPills, loading, refresh]);

  return (
    <TournamentCatalogContext.Provider value={value}>
      {children}
    </TournamentCatalogContext.Provider>
  );
};

export const useTournamentCatalog = () => {
  const ctx = useContext(TournamentCatalogContext);
  if (!ctx) return { ...fallbackCatalog(), skillFilterPills: [...SKILL_LEVELS, SKILL_FILTER_KIDS], loading: false, refresh: async () => {} };
  return ctx;
};
